import { Get, PostWithStatusReturn, Put } from "@configs/Axios";
import { PermissionsInfo } from "@custom-types/Types";
import { GetAccessibleOrganization, GetOrganization } from "./Organization";

export type GetPortalUserResponse = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  accountLanguage: string;
  phoneNumber: string;
  accountStatus: string;
  portalRole: string;
  role: string;
  isLocked: true;
  imageUrl: UserProfileImage;
  activeDate: number;
  createdDate: number;
  updatedDate: number;
  permissions: PermissionsInfo<boolean>;
  isLdapUser?: boolean;
  externalIdpEnabled?: boolean;
  externalIdpType?: "OKTA" | "KEYCLOAK" | "LDAP";
};

type UserProfileImage = {
  url: string;
  expiry: number;
};

export type GetUserSettings = {
  userId?: string;
  colorMode?: string;
  locale?: Language;
  dateTime?: DateTime;
  dashboardSequence?: Sequence;
  notification?: NotificationSettings;
};

type GetRolePermissionListResponse = {
  name: string;
  description: string;
  custom: boolean;
  orgId?: string;
  roleId: string;
  permissions: PermissionsInfo<boolean>;
};

export type NotificationSettings = {
  webpush: boolean;
  email: boolean;
  eventFilter: NotificationFilters;
};

export type NotificationFilters = {
  reportShare: boolean;
  // Note: Don't delete. Event notification can be used in future
  /*
  shoppingCartLoss: boolean;
  mes: boolean;
  temperature: boolean;
  slipAndFall: boolean;
  crowdSafety: boolean;
  forkliftSpeed: boolean;
  emergencyExit: boolean;
  stoppedVehicle: boolean;
  trafficJam: boolean;
  */
};
type DateTime = {
  mode: string;
  timezone: string;
};

type Language = {
  language: string;
};

type Sequence = {
  conversionTime: number;
  dashboards: Dashboards[];
};
type Dashboards = {
  id: string;
};

export type GetPartnerApp = {
  id: string;
  name: string;
  icon: string;
  url: string;
  desc: string;
  wicon: string;
  dicon: string;
};

export const userApi = {
  getUser: async (orgId: string) => {
    return Get<GetPortalUserResponse>(`/v1/organizations/${orgId}/user`);
  },
  getUserWithPermissions: async (orgId: string) => {
    return Get<GetPortalUserResponse>(
      `/v1/organizations/${orgId}/user?permissions=true`
    );
  },
  generateUserPermission: async (orgId: string) => {
    return PostWithStatusReturn(
      `/v1/organization-management/app-default-permission`,
      { orgId }
    );
  },
  getSettings: async (orgId: string) => {
    return Get<GetUserSettings>(`/v1/organizations/${orgId}/settings`);
  },
  update: async (req: GetUserSettings, orgId: string) => {
    return Put<GetUserSettings>(`/v1/organizations/${orgId}/settings`, req);
  },
  partner: async () => {
    return Get<GetPartnerApp[]>(`/partner-app`);
  },
  updateLastAccess: async () => {
    return Get<[]>(`/v1/auth/access-token/update-lastAccess`);
  },
  lastAccess: async () => {
    return Get<[]>(`/v1/auth/access-token/lastAccess`);
  },
  getRolePermissionList: async (orgId: string, roleId: string) => {
    if (orgId) {
      return Get<GetRolePermissionListResponse>(
        `/v1/organizations/${orgId}/roles/${roleId}`
      );
    }
    return "";
  },
  getOrganizations: async () => {
    return Get<GetOrganization[]>(`/v1/organizations`);
  },
  getAccessibleOrganizations: async () => {
    return Get<GetAccessibleOrganization[]>(`/v1/organizations/accessible`);
  },
  getLastAccessOrganization: async () => {
    return Get<Pick<GetOrganization, "orgId">>(
      `/v1/organizations/lastAccessOrg`
    );
  },
  changeOrganization: async (orgId: string) => {
    return Put<void>(`/v1/organizations/lastAccessOrg/${orgId}`);
  }
};
